// "New email" dialog — fresh outbound, no thread to reply to.
//
// Calls the same /crm/api/inbox/reply endpoint without a messageId,
// which the server treats as a fresh send via Graph's /sendMail. Lands in
// the shared mailbox's Sent folder; the inbox mirror picks it up on the
// next */5 cron tick and threads against any reply that comes back.
//
// Used from:
//   - Contact detail header ("Email")
//   - Contact-emails empty state ("Send first email")
//   - Inbox top bar ("New email")

import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Send, Loader2, Wand2, Sparkles, ChevronDown, AlertCircle, Check, Mail,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { inboxApi, snippetsApi, aiApi } from "@/lib/api";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** When launched from a contact page, pre-fills the To field. */
  defaultTo?: string;
  /** Optional contact id — used by the AI draft endpoint to pull context. */
  contactId?: string;
  /** Optional pre-filled subject (e.g. for sequence handoffs). */
  defaultSubject?: string;
}

export default function ComposeNewDialog({
  open, onOpenChange, defaultTo, contactId, defaultSubject,
}: Props) {
  const qc = useQueryClient();
  const [to, setTo] = useState(defaultTo ?? "");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(defaultSubject ?? "");
  const [body, setBody] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [fromMailbox, setFromMailbox] = useState<string>("");
  const [includeSignature, setIncludeSignature] = useState(true);
  const ref = useRef<HTMLTextAreaElement>(null);

  // List of mailboxes the current user can send as. Pass contactId so the
  // owner-aware default flips to the user's mailbox (e.g. jose@) when the
  // signed-in user owns this contact.
  const mailboxesQuery = useQuery({
    queryKey: ["inbox", "mailboxes", contactId ?? "_none"],
    queryFn: () => inboxApi.mailboxes(contactId),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Default the From picker to the API-suggested default the first time.
  useEffect(() => {
    if (mailboxesQuery.data && !fromMailbox) {
      setFromMailbox(mailboxesQuery.data.default);
    }
  }, [mailboxesQuery.data, fromMailbox]);

  // Reset on open with the latest props (so navigating between contacts
  // and re-opening picks up the new email).
  useEffect(() => {
    if (open) {
      setTo(defaultTo ?? "");
      setSubject(defaultSubject ?? "");
      setBody("");
      setCc("");
      setShowCc(false);
      setIncludeSignature(true);
    }
  }, [open, defaultTo, defaultSubject]);

  const splitAddrs = (s: string): string[] =>
    s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

  const send = useMutation({
    mutationFn: () =>
      inboxApi.reply({
        // No messageId → fresh send via Graph /sendMail
        to: splitAddrs(to),
        cc: splitAddrs(cc),
        subject: subject.trim(),
        body,
        fromMailbox: fromMailbox || undefined,
        includeSignature,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inbox", "threads"] });
      // Also refresh contact detail so the new outbound shows up
      if (contactId) {
        qc.invalidateQueries({ queryKey: ["contacts", "detail", contactId] });
      }
      setTimeout(() => onOpenChange(false), 1200);
    },
  });

  const draft = useMutation({
    mutationFn: () =>
      inboxApi.draft({
        to: splitAddrs(to),
        cc: splitAddrs(cc),
        subject: subject.trim(),
        body,
        fromMailbox: fromMailbox || undefined,
        includeSignature,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inbox", "threads"] });
      setTimeout(() => onOpenChange(false), 1200);
    },
  });

  // AI draft — only works when we have a contactId. Uses the FIRST-TOUCH
  // contact-draft endpoint (pulls form submission + notes), not the thread
  // draft endpoint (which requires an existing thread).
  const aiDraft = useMutation({
    mutationFn: async () => {
      if (!contactId) throw new Error("ai_draft_requires_contact_id");
      const r = await aiApi.draftForContact(contactId, body || undefined);
      return r;
    },
    onSuccess: (data) => {
      setBody(data.draft);
      setTimeout(() => ref.current?.focus(), 50);
    },
  });

  // Snippets — same library used by the inbox composer.
  const snippetsList = useMutation({
    mutationFn: () => snippetsApi.list(),
  });
  useEffect(() => {
    if (open) snippetsList.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const liveSnippets = (snippetsList.data?.snippets ?? []).filter((s) => s.is_active);

  const insertSnippet = (text: string) => {
    setBody((prev) => (prev ? prev + "\n\n" + text : text));
    setTimeout(() => ref.current?.focus(), 50);
  };

  const empty = !body.trim();
  const noRecipients = splitAddrs(to).length === 0;
  const noSubject = !subject.trim();
  const sending = send.isPending;
  const drafting = draft.isPending;
  const aiDrafting = aiDraft.isPending;

  const error = send.error ?? draft.error ?? aiDraft.error;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" /> New email
            <div className="ml-auto flex items-center gap-1.5 text-[11px] font-normal text-ink-muted">
              <span>From</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded border border-line bg-surface-panel px-2 py-0.5 font-mono text-[11px] text-ink hover:bg-surface-panel2"
                    disabled={mailboxesQuery.isLoading || (mailboxesQuery.data?.mailboxes.length ?? 0) <= 1}
                  >
                    <span>{fromMailbox || "—"}</span>
                    <ChevronDown className="h-3 w-3 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Send from</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {(mailboxesQuery.data?.mailboxes ?? []).map((m) => (
                    <DropdownMenuItem
                      key={m}
                      onClick={() => setFromMailbox(m)}
                      className="font-mono text-xs"
                    >
                      {m}{m === fromMailbox ? " ✓" : ""}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div className="flex items-center gap-2 rounded border border-danger/40 bg-danger/5 px-3 py-2 text-xs text-danger">
            <AlertCircle className="h-3.5 w-3.5" /> Couldn't send: {(error as Error).message}
          </div>
        )}
        {send.isSuccess && (
          <div className="flex items-center gap-2 rounded border border-success/40 bg-success/5 px-3 py-2 text-xs text-success">
            <Check className="h-3.5 w-3.5" /> Sent. The next 5-min mirror tick will show it in Sent.
          </div>
        )}
        {draft.isSuccess && (
          <div className="flex items-center gap-2 rounded border border-brand/40 bg-brand/5 px-3 py-2 text-xs text-brand-deep">
            <Check className="h-3.5 w-3.5" /> Saved to the shared Drafts folder.
          </div>
        )}

        <div className="space-y-2">
          {/* To */}
          <div className="flex items-center gap-2 border-b border-line py-1.5">
            <span className="w-12 shrink-0 text-[11px] font-medium text-ink-dim">To</span>
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com (comma-separate for multiple)"
              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-ink outline-none placeholder:text-ink-dim"
            />
            {!showCc && (
              <button
                onClick={() => setShowCc(true)}
                className="text-[11px] text-ink-muted hover:text-ink"
              >
                Add Cc
              </button>
            )}
          </div>
          {/* Cc */}
          {showCc && (
            <div className="flex items-center gap-2 border-b border-line py-1.5">
              <span className="w-12 shrink-0 text-[11px] font-medium text-ink-dim">Cc</span>
              <input
                type="text"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="cc@example.com"
                className="min-w-0 flex-1 border-0 bg-transparent text-sm text-ink outline-none placeholder:text-ink-dim"
              />
            </div>
          )}
          {/* Subject */}
          <div className="flex items-center gap-2 border-b border-line py-1.5">
            <span className="w-12 shrink-0 text-[11px] font-medium text-ink-dim">Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What's this about?"
              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-ink outline-none placeholder:text-ink-dim"
            />
          </div>
          {/* Body */}
          <Textarea
            ref={ref}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={9}
            placeholder="Type your message…"
            className="border-0 bg-transparent shadow-none focus:ring-0"
          />
        </div>

        {/* Action bar */}
        <div className="flex items-center gap-2 border-t border-line bg-surface-panel2/40 px-1 py-2">
          {contactId && (
            <Button
              size="sm"
              variant="ghost"
              className="gap-1.5 text-accent-purple hover:bg-accent-purple/10"
              disabled={aiDrafting}
              onClick={() => aiDraft.mutate()}
              title="Lumen drafts a first-touch reply from this contact's form submission + recent notes"
            >
              {aiDrafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
              <span>Draft with Lumen</span>
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" className="gap-1.5">
                <Sparkles className="h-3.5 w-3.5" />
                <span>Snippets</span>
                <ChevronDown className="h-3 w-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
              <DropdownMenuLabel>Insert a snippet</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {liveSnippets.length === 0 ? (
                <div className="px-2 py-2 text-xs text-ink-muted">No snippets — add some from any inbox thread.</div>
              ) : liveSnippets.map((s) => (
                <DropdownMenuItem
                  key={s.id}
                  onClick={() => insertSnippet(s.body)}
                  className="flex-col items-start"
                >
                  <span className="text-sm">{s.name}</span>
                  <span className="line-clamp-1 text-[11px] text-ink-dim">
                    {s.body.split("\n")[0]}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <label
            className="ml-auto inline-flex cursor-pointer items-center gap-1.5 text-[11px] text-ink-muted hover:text-ink"
            title="Append your saved signature to this email. Edit your signature in My settings → Email signature."
          >
            <input
              type="checkbox"
              checked={includeSignature}
              onChange={(e) => setIncludeSignature(e.target.checked)}
              className="h-3 w-3 rounded border-line"
            />
            <span>Include signature</span>
          </label>

          <span className="text-[11px] text-ink-dim">
            {empty ? "Empty" : `${body.length} chars`}
          </span>

          <Button
            size="sm"
            variant="ghost"
            disabled={empty || noRecipients || drafting || sending}
            onClick={() => draft.mutate()}
            className="gap-1.5"
          >
            {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Save draft
          </Button>
          <Button
            size="sm"
            disabled={empty || noRecipients || noSubject || sending || drafting}
            onClick={() => send.mutate()}
            title={noRecipients ? "Add at least one recipient" : noSubject ? "Add a subject" : "Send"}
            className="gap-1.5"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            <span>Send</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
